/**
 * Where a Gaps-layout block sits on its row's timeline, and whether it is too narrow to show its actions
 * until hovered.
 *
 * Pure and outside `DailyRow` so a caller that needs to know which blocks mount their actions (the call-off
 * walkthrough picks its target among them) computes it exactly the way the row renders it.
 */
import type { DateTime } from "luxon";
import { normalizeEnd, widthPercentToHideTextOnHover } from "../utils";

export type SlotLayout = {
	dayStart: DateTime;
	dayEnd: DateTime;
	/** The slot's start, clamped to the department's day start (overnight slots begin before it). */
	effectiveSlotStart: DateTime;
	/** Left offset of the slot within the day, as a % of the timeline. */
	offsetPct: number;
	/** What is left of the timeline after the offset, as a %. */
	containerWidthPct: number;
};

export const slotLayout = (slotStart: DateTime, date: DateTime, dayStartHour: number): SlotLayout => {
	const dayStart = date.startOf("day").set({ hour: dayStartHour, minute: 0, second: 0, millisecond: 0 });
	const dayEnd = dayStart.plus({ day: 1 });
	const effectiveSlotStart = slotStart < dayStart ? dayStart : slotStart;
	const offsetMinutes = effectiveSlotStart.diff(dayStart, "minutes").as("minutes");
	const offsetPct = Math.max((offsetMinutes / (24 * 60)) * 100, 0);
	return { dayStart, dayEnd, effectiveSlotStart, offsetPct, containerWidthPct: 100 - offsetPct };
};

/** A block's start/end on the timeline: the pre-normalized times when it has them, else its own. */
// DEFAULT: request and recommendation blocks carry no pre-normalized times (only shift items do), so their
// own start and an end normalized past midnight are the real position, not a guess.
export const normalizeTimes = (block: { start: DateTime; end: DateTime; normalizedStart?: DateTime; normalizedEnd?: DateTime }) => ({
	start: block.normalizedStart ?? block.start,
	end: block.normalizedEnd ?? normalizeEnd(block.start, block.end),
});

/** A block's width as a % of its slot's container, clipped at the end of the day. */
export const blockWidthPct = (normStart: DateTime, normEnd: DateTime, layout: SlotLayout): number => {
	const clippedNormEnd = normEnd > layout.dayEnd ? layout.dayEnd : normEnd;
	const hours = Math.max(clippedNormEnd.diff(normStart, "minutes").as("minutes") / 60, 0);
	return (hours / 24) * 100 * (100 / layout.containerWidthPct);
};

/** Narrow blocks hide their text on hover and mount their actions ONLY while hovered. */
export const hidesActionsUntilHover = (widthPct: number, disabledHideTextOnHover: boolean): boolean =>
	widthPct <= widthPercentToHideTextOnHover && !disabledHideTextOnHover;
